import { Bot, Database, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { TeamHub as TeamHubType } from "@/types";
import { timeAgo } from "./utils";

export function ResourcesTab({
  hub,
  navigate,
  onViewAllConversations,
}: Readonly<{
  hub: TeamHubType;
  navigate: ReturnType<typeof useNavigate>;
  onViewAllConversations?: () => void;
}>) {
  return (
    <div className="p-6 space-y-8">
      {/* Agents */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-content-primary">Team Agents</h2>
          {hub.agents.length > 0 && (
            <button
              onClick={() => navigate("/agents")}
              className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
            >
              View all
            </button>
          )}
        </div>
        {hub.agents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {hub.agents.map((agent) => (
              <button
                key={agent.uuid}
                onClick={() => navigate(`/chat?agent=${agent.uuid}`)}
                className="bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-border-secondary transition-colors text-left group"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${agent.color}20` }}
                  >
                    <Bot className="h-5 w-5" style={{ color: agent.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-content-primary text-sm truncate group-hover:text-brand-primary transition-colors">
                      {agent.name}
                    </h3>
                    {agent.description && (
                      <p className="text-xs text-content-tertiary line-clamp-2 mt-1">{agent.description}</p>
                    )}
                    <p className="text-xs text-content-quaternary mt-2">
                      {agent.usageCount} {agent.usageCount === 1 ? "use" : "uses"}
                      {agent.createdByName && ` · by ${agent.createdByName}`}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-surface-secondary/50 rounded-xl p-8 text-center">
            <Bot className="h-8 w-8 text-content-quaternary mx-auto mb-2" />
            <p className="text-sm text-content-tertiary">No agents in this team yet.</p>
          </div>
        )}
      </section>

      {/* Data Sources */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-content-primary">Data Sources</h2>
          {hub.dataSources.length > 0 && (
            <button
              onClick={() => navigate("/data-sources")}
              className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
            >
              View all
            </button>
          )}
        </div>
        {hub.dataSources.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hub.dataSources.map((ds) => (
              <button
                key={ds.uuid}
                onClick={() => navigate(`/data-sources/${ds.uuid}`)}
                className="bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-border-secondary transition-colors text-left group"
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
                    <Database className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-content-primary text-sm truncate group-hover:text-brand-primary transition-colors">
                      {ds.name}
                    </h3>
                    {ds.description && (
                      <p className="text-xs text-content-tertiary line-clamp-2 mt-1">{ds.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-content-quaternary">
                      <span className="capitalize">{ds.sourceType.replace("_", " ")}</span>
                      <span>{ds.fileCount} files</span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${(() => {
                          if (ds.status === "ready") return "bg-emerald-500/20 text-emerald-400";
                          if (ds.status === "error" || ds.status === "failed") return "bg-red-500/20 text-red-400";
                          return "bg-amber-500/20 text-amber-400";
                        })()}`}
                      >
                        {ds.status}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-surface-secondary/50 rounded-xl p-8 text-center">
            <Database className="h-8 w-8 text-content-quaternary mx-auto mb-2" />
            <p className="text-sm text-content-tertiary">No data sources in this team yet.</p>
          </div>
        )}
      </section>

      {/* Recent Conversations */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-content-primary">Recent Conversations</h2>
          {hub.recentChats.length > 0 && (
            <button
              onClick={onViewAllConversations}
              className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
            >
              View all
            </button>
          )}
        </div>
        {hub.recentChats.length > 0 ? (
          <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
            {hub.recentChats.map((chat, idx) => (
              <button
                key={chat.uuid}
                onClick={() => navigate(`/chat/${chat.uuid}`)}
                className={`w-full flex items-center gap-4 px-5 py-3.5 hover:bg-interactive-hover transition-colors text-left ${
                  idx < hub.recentChats.length - 1 ? "border-b border-border-primary" : ""
                }`}
              >
                <div className="h-9 w-9 rounded-lg bg-brand-primary/10 flex items-center justify-center shrink-0">
                  <MessageSquare className="h-4 w-4 text-brand-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-content-primary truncate">
                    {chat.title || "Untitled conversation"}
                  </p>
                  <p className="text-xs text-content-quaternary mt-0.5">
                    {chat.userName && `${chat.userName} · `}
                    {chat.agentName && `with ${chat.agentName} · `}
                    {chat.messageCount} messages
                  </p>
                </div>
                <span className="text-xs text-content-quaternary whitespace-nowrap shrink-0">
                  {timeAgo(chat.updatedAt)}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-surface-secondary/50 rounded-xl p-8 text-center">
            <MessageSquare className="h-8 w-8 text-content-quaternary mx-auto mb-2" />
            <p className="text-sm text-content-tertiary">No conversations in this team yet.</p>
          </div>
        )}
      </section>
    </div>
  );
}
